import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Clock3,
  Link2,
  PhoneMissed,
  Search,
  Star,
  Ticket,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { userHasAnyRole } from '../../roles';
import { useCallHistory } from '../hooks/useCallHistory';
import { callRecordingPresentation } from '../CallRecordingState';
import { telephonyApi } from '../telephonyApi';
import { CallRouteDetails } from './CallRouteDetails';
import { RecordingPlayer } from './RecordingPlayer';
import { CallClientLinkDialog } from '../../client-history/CallClientLinkDialog';
import type { BrowserCall } from '../types';

function duration(seconds = 0): string {
  const safe = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function dateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function statusLabel(value?: string): string {
  const labels: Record<string, string> = {
    ANSWERED: 'Отвечен',
    COMPLETED: 'Завершён',
    MISSED: 'Пропущен',
    FAILED: 'Ошибка',
    RINGING: 'Вызов',
    INITIATED: 'Создаётся',
    CANCELLED: 'Отменён',
  };
  return labels[String(value || '').toUpperCase()] || value || '—';
}

export function CallHistory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState<'' | 'INBOUND' | 'OUTBOUND'>('');
  const [status, setStatus] = useState('');
  const [routeGroupId, setRouteGroupId] = useState('');
  const [operatorUserId, setOperatorUserId] = useState('');
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [linkingCall, setLinkingCall] = useState<BrowserCall | null>(null);
  const [page, setPage] = useState(1);
  const history = useCallHistory({
    search,
    direction,
    status,
    routeGroupId,
    operatorUserId,
    page,
    pageSize: 20,
  });
  const filterOptions = useQuery({
    queryKey: ['telephony-call-filter-options'],
    queryFn: telephonyApi.callFilterOptions,
    staleTime: 60_000,
  });
  const data = history.data;
  const canProtectRecording = userHasAnyRole(user, ['ADMIN', 'MANAGER']);
  const canLinkClient = user?.userType === 'EMPLOYEE' && userHasAnyRole(user, ['ADMIN', 'MANAGER', 'DISPATCHER', 'EXECUTOR']);
  const importanceMutation = useMutation({
    mutationFn: ({ callId, important }: { callId: string; important: boolean }) => telephonyApi.setRecordingImportance(callId, important),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
    },
  });

  return <section className="telephony-history-module">
    <div className="telephony-history-toolbar">
      <label className="search-field"><Search /><input value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} placeholder="Номер, имя, компания" /></label>
      <select value={direction} onChange={event => { setDirection(event.target.value as '' | 'INBOUND' | 'OUTBOUND'); setPage(1); }}>
        <option value="">Все направления</option>
        <option value="INBOUND">Входящие</option>
        <option value="OUTBOUND">Исходящие</option>
      </select>
      <select value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
        <option value="">Все результаты</option>
        <option value="ANSWERED">Ответенные</option>
        <option value="COMPLETED">Завершённые</option>
        <option value="MISSED">Пропущенные</option>
        <option value="FAILED">Ошибочные</option>
      </select>
      <select
        value={routeGroupId}
        disabled={filterOptions.isLoading}
        onChange={event => { setRouteGroupId(event.target.value); setPage(1); }}
        aria-label="Фильтр по группе маршрута"
      >
        <option value="">{filterOptions.isLoading ? 'Загружаем группы…' : 'Все группы'}</option>
        {filterOptions.data?.groups.map(group => <option key={group.id} value={group.id}>{group.name || 'Группа без названия'}</option>)}
      </select>
      <select
        value={operatorUserId}
        disabled={filterOptions.isLoading}
        onChange={event => { setOperatorUserId(event.target.value); setPage(1); }}
        aria-label="Фильтр по сотруднику маршрута"
      >
        <option value="">{filterOptions.isLoading ? 'Загружаем сотрудников…' : 'Все сотрудники'}</option>
        {filterOptions.data?.operators.map(operator => <option key={operator.id} value={operator.id}>
          {operator.fullName || 'Сотрудник без имени'}{operator.extension ? ` · доб. ${operator.extension}` : ''}
        </option>)}
      </select>
    </div>

    {history.isLoading && <div className="telephony-history-state">Загружаем журнал звонков…</div>}
    {history.isError && <div className="telephony-history-state error">{history.error instanceof Error ? history.error.message : 'Не удалось загрузить журнал'}</div>}
    {filterOptions.isError && <div className="telephony-history-inline-error">Не удалось загрузить группы и сотрудников для фильтра. Сам журнал продолжает работать.</div>}
    {importanceMutation.isError && <div className="telephony-history-inline-error">{importanceMutation.error instanceof Error ? importanceMutation.error.message : 'Не удалось изменить защиту записи'}</div>}
    {!history.isLoading && !history.isError && !data?.rows.length && <div className="telephony-history-state"><PhoneMissed />Звонки по заданным условиям не найдены</div>}

    {Boolean(data?.rows.length) && <div className="telephony-history-table">
      <div className="telephony-history-head">
        <span>Звонок</span><span>Контакт</span><span>Связи</span><span>Дата</span><span>Длительность</span><span>Статус</span><span>Запись</span><span>Маршрут</span>
      </div>
      {data?.rows.map(call => {
        const number = call.direction === 'INBOUND' ? call.from_number || call.to_number : call.to_number || call.from_number;
        const recording = callRecordingPresentation(call);
        const canToggleImportance = canProtectRecording && (call.recording_status === 'AVAILABLE' || call.recording_status === 'RECORDING');
        const importancePending = importanceMutation.isPending && importanceMutation.variables?.callId === call.id;
        return <article key={call.id} className="telephony-history-row">
          <span className="telephony-history-call">
            <i>{call.direction === 'INBOUND' ? <ArrowDownLeft /> : <ArrowUpRight />}</i>
            <span><b>{number || 'Номер не определён'}</b><small>{call.direction === 'INBOUND' ? 'Входящий' : 'Исходящий'}</small></span>
          </span>
          <span>{call.contact_id ? <Link className="telephony-history-contact" to={`/contacts?contact=${call.contact_id}`}><b>{call.contact_name || 'Контакт'}</b></Link> : <b>{call.contact_name || call.operator_name || 'Не определён'}</b>}<small><UserRound /> {call.extension ? `доб. ${call.extension}` : 'без добавочного'}</small></span>
          <span className="telephony-history-links">
            {call.company_id ? <Link to={`/companies?company=${call.company_id}`}><Building2 />{call.company_name || 'Компания'}</Link> : <small>Компания не привязана</small>}
            {call.ticket_id ? <Link to={`/tickets/${call.ticket_id}`}><Ticket />{call.ticket_display_number || 'Заявка'}</Link> : <small>Заявка не привязана</small>}
            {canLinkClient && <button type="button" className="telephony-history-client-link" onClick={() => setLinkingCall(call)}><Link2 />{call.company_id && call.contact_id ? 'Изменить клиента' : 'Привязать клиента'}</button>}
          </span>
          <span><b>{dateTime(call.started_at)}</b><small><Clock3 /> {call.answered_at ? `ответ ${dateTime(call.answered_at)}` : 'без ответа'}</small></span>
          <span><b>{duration(call.duration_seconds)}</b></span>
          <span><i className={`telephony-call-status status-${String(call.status || '').toLowerCase()}`}>{statusLabel(call.status)}</i></span>
          <span className="telephony-history-recording-cell"><span>{recording.kind === 'PLAYER'
            ? <RecordingPlayer src={recording.url} label={recording.label} durationSeconds={call.duration_seconds} />
            : <small>{recording.label}</small>}</span>
            {canToggleImportance && <button
              type="button"
              className={`recording-importance-button ${call.recording_is_important ? 'active' : ''}`}
              disabled={importancePending}
              aria-label={call.recording_is_important ? 'Снять защиту важной записи' : 'Защитить важную запись'}
              title={call.recording_is_important ? 'Важная запись — не удалять' : 'Пометить как важную и исключить из очистки'}
              onClick={() => importanceMutation.mutate({ callId: call.id, important: !call.recording_is_important })}
            ><Star fill={call.recording_is_important ? 'currentColor' : 'none'} /></button>}
          </span>
          <CallRouteDetails
            callId={call.id}
            trace={call.route_trace}
            expanded={expandedCallId === call.id}
            onToggle={() => setExpandedCallId(current => current === call.id ? null : call.id)}
          />
        </article>;
      })}
    </div>}

    <div className="telephony-history-pagination">
      <button type="button" className="button ghost" disabled={!data?.hasPrevious || history.isFetching} onClick={() => setPage(value => Math.max(1, value - 1))}><ArrowLeft /> Назад</button>
      <span>Страница {data?.page || page}{history.isFetching ? ' · обновление…' : ''}</span>
      <button type="button" className="button ghost" disabled={!data?.hasNext || history.isFetching} onClick={() => setPage(value => value + 1)}>Вперёд <ArrowRight /></button>
    </div>
    {linkingCall && <CallClientLinkDialog call={linkingCall} onClose={() => setLinkingCall(null)} onLinked={() => setLinkingCall(null)} />}
  </section>;
}
